import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useComputedColorScheme } from '@mantine/core';
import { Logo } from '../../components/app/Logo';
import { AppEntryButton, useAppEntry } from '../../components/app/AppEntry';
import { IconClock, IconSparkles } from '@tabler/icons-react';

// Policy sections — kept as Russian content (policy is a jurisdiction-specific
// legal document, not interface copy, so it stays inline rather than going
// through i18n).
const SECTIONS = [
  { id: 'general', n: '1', title: 'Общие положения' },
  { id: 'data', n: '2', title: 'Какие данные мы обрабатываем' },
  { id: 'purpose', n: '3', title: 'Цели обработки' },
  { id: 'legal', n: '4', title: 'Правовые основания' },
  { id: 'storage', n: '5', title: 'Хранение и сроки' },
  { id: 'sharing', n: '6', title: 'Передача третьим лицам' },
  { id: 'rights', n: '7', title: 'Права пользователей' },
  { id: 'security', n: '8', title: 'Безопасность данных' },
  { id: 'cookies', n: '9', title: 'Cookies и аналитика' },
  { id: 'changes', n: '10', title: 'Изменения в политике' },
  { id: 'contacts', n: '11', title: 'Контакты' },
];

const Pill: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: 999,
      border: '1px solid var(--line-strong)',
      background: 'var(--surface)',
      color: 'var(--ink-2)',
      fontSize: 12,
      fontWeight: 500,
      ...style,
    }}
  >
    {children}
  </span>
);

const TopBar: React.FC = () => (
  <header
    style={{
      position: 'sticky',
      top: 0,
      zIndex: 20,
      background: 'var(--bg)',
      backdropFilter: 'saturate(1.4) blur(10px)',
      borderBottom: '1px solid var(--line)',
    }}
  >
    <div
      style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '14px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      <Logo subtitle="Политика" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Link
          to="/docs"
          style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 12px', borderRadius: 8 }}
        >
          Документация
        </Link>
        <AppEntryButton />
      </div>
    </div>
  </header>
);

const Hero: React.FC = () => (
  <div style={{ padding: '48px 0 32px', borderBottom: '1px solid var(--line)', marginBottom: 40 }}>
    <Pill style={{ marginBottom: 16 }}>
      <IconClock size={12} /> Действует с 30 сентября 2026 · версия 3.2
    </Pill>
    <h1
      style={{
        fontSize: 42,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        marginBottom: 16,
        maxWidth: 720,
        color: 'var(--ink)',
      }}
    >
      Политика конфиденциальности
    </h1>
    <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.6, maxWidth: 680, margin: 0 }}>
      Этот документ описывает, какие данные SkillScape собирает о пользователях, зачем и как мы их
      защищаем. Мы стараемся писать честно и без юридического жаргона — там, где это возможно.
    </p>
  </div>
);

const Toc: React.FC<{ active: string; onPick: (id: string) => void }> = ({ active, onPick }) => (
  <aside style={{ position: 'sticky', top: 88, paddingTop: 8 }}>
    <div
      style={{
        fontSize: 10,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'var(--soft)',
        fontWeight: 600,
        marginBottom: 12,
      }}
    >
      Содержание
    </div>
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, borderLeft: '1px solid var(--line)' }}>
      {SECTIONS.map((s) => {
        const isActive = s.id === active;
        return (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                onPick(s.id);
              }}
              style={{
                display: 'block',
                padding: '7px 0 7px 14px',
                marginLeft: -1,
                borderLeft: '2px solid ' + (isActive ? 'var(--accent)' : 'transparent'),
                fontSize: 13,
                color: isActive ? 'var(--accent-deep)' : 'var(--muted)',
                fontWeight: isActive ? 500 : 400,
                transition: 'color 120ms, border-color 120ms',
              }}
            >
              <span
                style={{ marginRight: 8, color: 'var(--soft)', fontVariantNumeric: 'tabular-nums' }}
              >
                {s.n}.
              </span>
              {s.title}
            </a>
          </li>
        );
      })}
    </ul>
  </aside>
);

const Section: React.FC<{ id: string; n: string; title: string; children: React.ReactNode }> = ({
  id,
  n,
  title,
  children,
}) => (
  <section id={id} style={{ marginBottom: 48, scrollMarginTop: 96 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 16 }}>
      <span
        style={{
          fontSize: 13,
          color: 'var(--soft)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {n}
      </span>
      <h2
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'var(--ink)',
          margin: 0,
        }}
      >
        {title}
      </h2>
    </div>
    <div style={{ paddingLeft: 28 }}>{children}</div>
  </section>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--ink-2)', margin: '0 0 14px' }}>
    {children}
  </p>
);
const L: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--ink-2)', marginBottom: 6 }}>
    {children}
  </li>
);
const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul style={{ paddingLeft: 22, marginBottom: 14 }}>{children}</ul>
);

const HighlightBox: React.FC<{
  tone?: 'accent' | 'warn';
  title: string;
  children: React.ReactNode;
}> = ({ tone = 'accent', title, children }) => {
  const tones = {
    accent: { bg: 'var(--accent-soft)', fg: 'var(--accent-deep)' },
    warn: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  };
  const tt = tones[tone];
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        padding: '16px 18px',
        background: tt.bg,
        borderRadius: 10,
        marginBottom: 16,
      }}
    >
      <div style={{ flexShrink: 0, color: tt.fg, marginTop: 2 }}>
        <IconSparkles size={18} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: tt.fg, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
};

const DataTable: React.FC<{ rows: { cat: string; what: string; ttl: string }[] }> = ({ rows }) => (
  <div
    style={{
      border: '1px solid var(--line)',
      borderRadius: 10,
      overflow: 'hidden',
      background: 'var(--surface)',
      marginBottom: 16,
    }}
  >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 2fr 1fr',
        gap: 16,
        padding: '12px 16px',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--line)',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      }}
    >
      <div>Категория</div>
      <div>Что входит</div>
      <div>Срок хранения</div>
    </div>
    {rows.map((r, i) => (
      <div
        key={r.cat}
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 2fr 1fr',
          gap: 16,
          padding: '14px 16px',
          borderBottom: i < rows.length - 1 ? '1px solid var(--line)' : 'none',
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{r.cat}</div>
        <div style={{ color: 'var(--ink-2)', lineHeight: 1.5 }}>{r.what}</div>
        <div style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{r.ttl}</div>
      </div>
    ))}
  </div>
);

const ContactGrid: React.FC = () => {
  // Один адрес на всё: запросы по данным, безопасность, договоры и вопросы по продукту.
  const rows = [
    {
      who: 'SkillScape',
      desc: 'Запросы по персональным данным, безопасность, договоры и вопросы по продукту',
      email: 'hello@skillscape.ru',
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 12,
        marginBottom: 16,
      }}
    >
      {rows.map((r) => (
        <div
          key={r.who}
          style={{
            padding: 16,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>
            {r.who}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{r.desc}</div>
          <a
            href={`mailto:${r.email}`}
            style={{ fontSize: 12, color: 'var(--accent-deep)', fontWeight: 500 }}
          >
            {r.email}
          </a>
        </div>
      ))}
    </div>
  );
};

export const Policy: React.FC = () => {
  const entry = useAppEntry();
  const [active, setActive] = useState('general');
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';

  // Scroll-spy via IntersectionObserver, plus a bottom-of-page fallback so the
  // last section (often too short for the observer band) becomes active when
  // the user scrolls to the very bottom.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: '-30% 0px -60% 0px' },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });

    const onScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (nearBottom) setActive(SECTIONS[SECTIONS.length - 1].id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="policy-page"
      data-theme={isDark ? 'dark' : 'light'}
      style={{ minHeight: '100vh', background: 'var(--bg)' }}
    >
      <style>{`
        .policy-page {
          --bg: #F8F9FA; --surface: #FFFFFF; --surface-2: #F1F3F5;
          --line: #E9ECEF; --line-strong: #DEE2E6;
          --ink: #212529; --ink-2: #495057; --muted: #868E96; --soft: #ADB5BD;
          --accent: #12B886; --accent-soft: #E6FCF5; --accent-deep: #099268;
          --warn: #F08C00; --warn-soft: #FFF4E6;
          color: var(--ink);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 14px;
          -webkit-font-smoothing: antialiased;
        }
        .policy-page[data-theme='dark'] {
          --bg: #1A1B1E; --surface: #25262B; --surface-2: #2C2E33;
          --line: #373A40; --line-strong: #5C5F66;
          --ink: #C1C2C5; --ink-2: #A6A7AB; --muted: #909296; --soft: #5C5F66;
          --accent: #20C997; --accent-soft: #0C3B30; --accent-deep: #63E6BE;
          --warn: #FFA94D; --warn-soft: #3B2A18;
        }
        .policy-page a { text-decoration: none; }
        .policy-page a:hover { opacity: 0.85; }
      `}</style>

      <TopBar />

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 40px',
          display: 'grid',
          gridTemplateColumns: '260px minmax(0, 1fr)',
          gap: 56,
          alignItems: 'flex-start',
        }}
      >
        <Toc active={active} onPick={scrollTo} />
        <main style={{ paddingBottom: 80 }}>
          <Hero />

          <Section id="general" n="1" title="Общие положения">
            <P>
              Настоящая Политика регулирует обработку персональных данных пользователей сервиса
              SkillScape (далее — «Сервис»), оператором которого является ООО «Кубит» (далее —
              «Оператор»).
            </P>
            <P>
              Использование Сервиса означает, что пользователь ознакомился с этой Политикой и
              принимает её. Если ты не согласен с какими-то её положениями — не используй Сервис и
              закрой эту страницу.
            </P>
            <HighlightBox title="Коротко по-человечески">
              Мы собираем минимум: то, без чего платформа не сможет работать или показывать тебе
              твой прогресс. Мы не продаём данные рекламодателям и не используем их для тренировки
              сторонних моделей.
            </HighlightBox>
          </Section>

          <Section id="data" n="2" title="Какие данные мы обрабатываем">
            <P>
              Конкретный набор зависит от того, как ты пользуешься платформой. Ниже — полная картина
              по категориям.
            </P>
            <DataTable
              rows={[
                {
                  cat: 'Учётные',
                  what: 'Имя, рабочий email, должность, команда, роль (сотрудник/менеджер/админ).',
                  ttl: 'до удаления аккаунта',
                },
                {
                  cat: 'Профессиональные',
                  what: 'Грейды, подтверждённые компетенции, цели, прогресс по обучению.',
                  ttl: '5 лет',
                },
                {
                  cat: 'Контент ревью',
                  what: 'Self-эссе, peer-обратная связь, саммари руководителя.',
                  ttl: '5 лет',
                },
                {
                  cat: 'Технические',
                  what: 'IP-адрес, тип устройства, user-agent, ID сессии.',
                  ttl: '90 дней',
                },
                {
                  cat: 'Аналитика',
                  what: 'Анонимизированные события: переходы по страницам, время ответа форм.',
                  ttl: '2 года',
                },
                {
                  cat: 'Заявки с сайта',
                  what: 'Имя, рабочий email, название и размер компании из формы на skillscape.ru.',
                  ttl: '1 год',
                },
                {
                  cat: 'Посещения сайта',
                  what: 'Cookie-идентификатор, IP-адрес, устройство, просмотренные страницы, клики и прокрутка, записи сессий (Яндекс Метрика).',
                  ttl: 'по правилам Яндекс Метрики',
                },
              ]}
            />
            <P>
              Мы не запрашиваем и не храним: банковские реквизиты сотрудников, паспортные данные,
              медицинскую информацию, политические или религиозные взгляды.
            </P>
          </Section>

          <Section id="purpose" n="3" title="Цели обработки">
            <P>Данные обрабатываются строго для того, чтобы Сервис выполнял свои функции:</P>
            <UL>
              <L>предоставлять доступ к личному кабинету и карте компетенций;</L>
              <L>проводить раунды Performance Review и 360°-опросов;</L>
              <L>показывать аналитику по росту команды руководителям;</L>
              <L>отправлять уведомления о дедлайнах и изменениях, если ты их не отключил;</L>
              <L>обеспечивать безопасность платформы (логирование входов, обнаружение аномалий);</L>
              <L>отвечать на заявки с сайта: связаться, провести демо, открыть пространство;</L>
              <L>понимать, как посетители пользуются сайтом, и улучшать его.</L>
            </UL>
          </Section>

          <Section id="legal" n="4" title="Правовые основания">
            <P>Обработка данных ведётся на основании:</P>
            <UL>
              <L>
                трудовых отношений между пользователем и компанией-клиентом SkillScape (ст. 86 ТК
                РФ);
              </L>
              <L>согласия пользователя при регистрации;</L>
              <L>согласия посетителя сайта при отправке заявки;</L>
              <L>исполнения договора между Оператором и компанией-клиентом.</L>
            </UL>
          </Section>

          <Section id="storage" n="5" title="Хранение и сроки">
            <P>
              Все данные хранятся на серверах в РФ (дата-центр Yandex Cloud, регион ru-central1).
              Резервные копии шифруются и хранятся 30 дней.
            </P>
            <P>
              После увольнения сотрудника из компании-клиента его данные обезличиваются в течение 30
              дней или удаляются полностью по запросу. Содержимое анонимных peer-ответов остаётся в
              саммари без привязки к автору.
            </P>
          </Section>

          <Section id="sharing" n="6" title="Передача третьим лицам">
            <P>Мы не передаём персональные данные третьим лицам, за исключением случаев:</P>
            <UL>
              <L>
                <strong>Подрядчики Оператора</strong> — облачный хостинг и email-провайдер. С каждым
                подписаны DPA.
              </L>
              <L>
                <strong>ООО «Яндекс»</strong> — сервис Яндекс Метрика: статистика посещений сайта
                skillscape.ru и платформы по{' '}
                <a
                  href="https://yandex.ru/legal/metrica_termsofuse/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent-deep)', fontWeight: 500 }}
                >
                  условиям использования Метрики
                </a>
                .
              </L>
              <L>
                <strong>Telegram</strong> — заявки с сайта приходят сотрудникам Оператора в рабочий
                чат Telegram, чтобы на них быстро ответить. Серверы Telegram находятся за пределами
                РФ, поэтому это трансграничная передача данных.
              </L>
              <L>
                <strong>Государственные органы</strong> — только по официальному запросу и в рамках
                закона.
              </L>
              <L>
                <strong>Преемник при реорганизации</strong> — с уведомлением пользователей за 30
                дней.
              </L>
            </UL>
            <HighlightBox tone="warn" title="Что мы НЕ делаем">
              Не продаём данные рекламным сетям. Не передаём содержимое ревью никому, кроме
              предусмотренной ролевой моделью аудитории (сам сотрудник, его руководитель, HR).
            </HighlightBox>
          </Section>

          <Section id="rights" n="7" title="Права пользователей">
            <P>Ты имеешь право:</P>
            <UL>
              <L>получить полную копию своих данных в машиночитаемом формате (JSON);</L>
              <L>исправить неточности в учётной информации напрямую через Настройки;</L>
              <L>запросить удаление аккаунта (выполняется в течение 30 дней);</L>
              <L>отозвать согласие на обработку — это автоматически блокирует учётную запись;</L>
              <L>пожаловаться в Роскомнадзор, если считаешь, что мы нарушаем твои права.</L>
            </UL>
            <P>
              Запросы по правам субъектов данных принимаются на{' '}
              <a
                href="mailto:hello@skillscape.ru"
                style={{ color: 'var(--accent-deep)', fontWeight: 500 }}
              >
                hello@skillscape.ru
              </a>
              . Срок ответа — 10 рабочих дней.
            </P>
          </Section>

          <Section id="security" n="8" title="Безопасность данных">
            <P>Что мы делаем для защиты твоих данных:</P>
            <UL>
              <L>TLS 1.3 для всего трафика между браузером и серверами;</L>
              <L>шифрование at-rest для базы данных и резервных копий (AES-256);</L>
              <L>двухфакторная аутентификация по email-коду (по умолчанию);</L>
              <L>опциональный SSO через SAML 2.0 для корпоративных клиентов;</L>
              <L>
                принцип минимальных привилегий: сотрудники Оператора имеют доступ к prod-данным
                только при инцидентах, по запросу и с аудитом;
              </L>
              <L>ежегодный pen-test внешним подрядчиком.</L>
            </UL>
          </Section>

          <Section id="cookies" n="9" title="Cookies и аналитика">
            <P>Сервис использует cookies трёх типов:</P>
            <UL>
              <L>
                <strong>Технические</strong> — без них платформа не работает (сессия, CSRF-токен).
                Отключить нельзя.
              </L>
              <L>
                <strong>Аналитические</strong> — Яндекс Метрика: показывает, как пользуются сайтом и
                платформой, какие разделы работают, а какие нет. Подробнее и как отключить — ниже.
              </L>
              <L>
                <strong>Функциональные</strong> — запоминают тему, язык, размер шрифта. Хранятся
                локально, не уходят на сервер.
              </L>
            </UL>
            <P>
              На сайте skillscape.ru работает Яндекс Метрика; в самой платформе после входа она тоже
              может быть включена. Метрика ставит свои cookies и собирает статистику: откуда пришёл
              посетитель, какие разделы смотрел, на что нажимал. Вебвизор записывает сессии, чтобы
              мы видели, где людям неудобно. На главной странице содержимое полей формы заявки (имя,
              email, компания) в запись не попадает. В платформе в запись попадает то, что видно на
              экране, — в том числе эссе, отзывы и оценки; имя и email в Метрику отдельно не
              передаются, а доступ к записям мы оставляем только сотрудникам Оператора, которым
              разрешено видеть эти данные.
            </P>
            <P>
              Чтобы Метрика тебя не учитывала, запрети cookies для сайта в настройках браузера или
              поставь{' '}
              <a
                href="https://yandex.ru/support/metrica/general/opt-out.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent-deep)', fontWeight: 500 }}
              >
                блокировщик Яндекс Метрики
              </a>
              .
            </P>
          </Section>

          <Section id="changes" n="10" title="Изменения в политике">
            <P>
              Если мы существенно меняем эту политику — присылаем email всем активным пользователям
              минимум за 14 дней до вступления изменений в силу.
            </P>
            <P>
              Косметические правки (опечатки, переформулировки без изменения смысла) могут вступать
              в силу без отдельного уведомления.
            </P>
          </Section>

          <Section id="contacts" n="11" title="Контакты">
            <ContactGrid />
          </Section>

          <div
            style={{
              marginTop: 64,
              paddingTop: 28,
              borderTop: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 12,
              color: 'var(--muted)',
            }}
          >
            <div>© 2026 ООО «Кубит» · ИНН 4217206100</div>
            <div style={{ display: 'flex', gap: 20 }}>
              <Link to="/docs" style={{ color: 'inherit' }}>
                Документация
              </Link>
              <Link to={entry.to} style={{ color: 'inherit' }}>
                {entry.label}
              </Link>
              <a href="mailto:hello@skillscape.ru" style={{ color: 'inherit' }}>
                hello@skillscape.ru
              </a>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Policy;
